import React, {useRef, useEffect} from 'react';
import {Animated, StyleSheet, View, Platform} from 'react-native';
import SkillzImage from 'SkillzImage';
import {SkillzText} from '@skillz/ui';
import ThemeOverrideProps from 'ThemeOverrideProps';
import RewardItem from './RewardItem';
import {DepositSuccessModalDeposit} from 'DepositSuccessModal';
import LottieView from 'lottie-react-native';
import {_m} from 'INTL';

const {fontSemiBold} = ThemeOverrideProps.sheetStyles;

type AdditionalRewardsProps = {
  delay?: number;
  deposit: DepositSuccessModalDeposit;
};

const AdditionalRewards = ({delay = 2000, deposit}: AdditionalRewardsProps) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(100)).current;
  const isIOS = Platform.OS === 'ios';

  const lottiePortalRef = useRef<LottieView>(null);
  const lottieCashNotesRef = useRef<LottieView>(null);
  const lottieTicketzRef = useRef<LottieView>(null);
  const lottieZRef = useRef<LottieView>(null);
  const lottieFilePrefix = '../../../../../../../../source_images/lottieAnimations/';

  const portalLottieFileIOS = require(`${lottieFilePrefix}/deposit_success_portal_ios.json`);

  const portalLottieFileAndroid = require(
    `${lottieFilePrefix}/deposit_success_portal_android.json`
  );
  const cashLottieFile = require(`${lottieFilePrefix}/deposit_success_flying_cash.json`);
  const ticketzLottieFile = require(`${lottieFilePrefix}/deposit_success_flying_ticketz.json`);

  const zLottieFile = require(`${lottieFilePrefix}/deposit_success_flying_z_coins.json`);

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1, // Final opacity
      duration: 500, // Duration in ms
      delay,
      useNativeDriver: true, // Use native driver for better perf
    }).start();

    Animated.timing(translateY, {
      toValue: 0,
      duration: 500,
      delay,
      useNativeDriver: true,
    }).start(() => {
      if (deposit?.bonusCash > 0) {
        lottieCashNotesRef.current?.play();
      } else if (deposit.ticketz) {
        lottieTicketzRef.current?.play();
      } else if (deposit.points) {
        lottieZRef.current?.play();
      }
    });

    // infinite rotation
    Animated.loop(
      Animated.sequence([
        Animated.timing(rotateAnim, {
          toValue: 1,
          duration: 3000, // spin duration
          useNativeDriver: true,
        }),
        Animated.delay(1000), // ⏸ pause for 1s
        Animated.timing(rotateAnim, {
          toValue: 0, // reset back
          duration: 0, // instant reset
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [fadeAnim, delay]);

  // Map 0 → 1 to 0deg → 360deg
  const spinRight = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  const spinLeft = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['360deg', '0deg'],
  });

  const handleRewardItemFinished = (name: string) => {
    const isCash = name === 'bonusCash';
    const isTicketz = name === 'ticketz';
    const isPoints = name === 'points';
    if (isCash) {
      if (deposit?.ticketz || deposit?.points) {
        setTimeout(() => {
          deposit?.ticketz ? lottieTicketzRef?.current?.play() : lottieZRef?.current?.play();
        }, 100);

        setTimeout(
          () => {
            lottiePortalRef?.current?.reset();
          },
          isIOS ? 2800 : 6000
        );
      } else {
        setTimeout(() => {
          lottiePortalRef?.current?.reset();
        }, 500);
      }
    }

    if (isTicketz) {
      if (deposit?.points) {
        setTimeout(() => {
          lottieZRef?.current?.play();
        }, 100);

        setTimeout(
          () => {
            lottiePortalRef?.current?.reset();
          },
          isIOS ? 2800 : 6000
        );
      } else {
        setTimeout(() => {
          lottiePortalRef?.current?.reset();
        }, 500);
      }
    }

    if (isPoints) {
      if (deposit?.ticketz) {
        setTimeout(() => {
          lottieTicketzRef?.current?.play();
        }, 100);

        setTimeout(
          () => {
            lottiePortalRef?.current?.reset();
          },
          isIOS ? 2800 : 6000
        );
      } else {
        setTimeout(() => {
          lottiePortalRef?.current?.reset();
        }, 500);
      }
    }
  };

  const promoCouponCashAmount = deposit.promoCouponCashAmount ?? 0;
  const totalBonusValue = deposit.bonusCash + deposit.promoCash + (deposit.bankPromoCash ?? 0) + promoCouponCashAmount;

  return (
    <Animated.View style={[styles.container, {opacity: fadeAnim, transform: [{translateY}]}]}>
      <View style={styles.titleContainer}>
        <Animated.View style={{transform: [{rotate: spinRight}]}}>
          <SkillzImage source='icon_shining_star_white.png' style={styles.shiningStarIcon} />
        </Animated.View>
        <SkillzText style={styles.additionalRewardsText}>{_m('Additional Rewards')}</SkillzText>
        <Animated.View style={{transform: [{rotate: spinLeft}]}}>
          <SkillzImage source='icon_shining_star_white.png' style={styles.shiningStarIcon} />
        </Animated.View>
      </View>
      <View style={styles.rewardContainer}>
        {deposit.bonusCash > 0 && (
          <RewardItem
            reward={totalBonusValue}
            delay={4100}
            type={'bonusCash'}
            finishedCallback={handleRewardItemFinished}
          />
        )}
        {!!deposit.ticketz && (
          <RewardItem
            reward={deposit.ticketz}
            delay={deposit.bonusCash ? 6000 : 4100}
            type={'ticketz'}
            finishedCallback={handleRewardItemFinished}
          />
        )}
        {/* TODO: Add Z icon for this reward item */}
        {!!deposit.points && (
          <RewardItem
            reward={deposit.points}
            delay={deposit.bonusCash || deposit.ticketz ? 6000 : 4100}
            type={'points'}
            finishedCallback={handleRewardItemFinished}
          />
        )}
      </View>

      <LottieView
        source={isIOS ? portalLottieFileIOS : portalLottieFileAndroid}
        loop={false}
        autoPlay={true}
        speed={isIOS ? 0.8 : 0.7}
        style={isIOS ? styles.lottieAnimationPortal : styles.lottieAnimationPortalAndroid}
        ref={lottiePortalRef}
      />

      <LottieView
        source={cashLottieFile}
        loop={false}
        autoPlay={false}
        speed={1.8}
        style={styles.lottieAnimationCashNotes}
        ref={lottieCashNotesRef}
      />

      <LottieView
        source={ticketzLottieFile}
        loop={false}
        autoPlay={false}
        speed={1.8}
        style={styles.lottieAnimationCashNotes}
        ref={lottieTicketzRef}
      />

      <LottieView
        source={zLottieFile}
        loop={false}
        autoPlay={false}
        speed={1.8}
        style={styles.lottieAnimationCashNotes}
        ref={lottieZRef}
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flexDirection: 'column',
    paddingTop: 26,
  },

  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 20,
  },

  shiningStarIcon: {
    width: 11,
    height: 11,
  },

  additionalRewardsText: {
    fontSize: 18,
    ...fontSemiBold,
    color: '#FFFFFF',
  },

  rewardContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },

  lottieAnimationPortal: {
    position: 'absolute',
    width: '100%',
    top: -100,
    zIndex: 10000,
  },

  lottieAnimationPortalAndroid: {
    position: 'absolute',
    width: '100%',
    top: 20,
    zIndex: 10000,
  },

  lottieAnimationCashNotes: {
    position: 'absolute',
    width: '100%',
    top: 60,
    zIndex: 10000,
  },
});

export default AdditionalRewards;
